<template lang="pug">
    AiToolsTabs.knowledge-base
        el-card.contains-table(shadow="always")
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .not-configured(v-else-if="!settingsLoading && !knowledgeSourceId")
                p {{ $t('No knowledge source configured for this CMS.') }}
            .entries-table(v-else)
                paginated-items(
                    scope="ex"
                    type="chatbot_knowledge_document"
                    :loading="loading"
                    :items="knowledgeDocumentsPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="knowledgeDocumentsPaginated.variables"
                    :all-columns="knowledgeDocumentsPaginated.allColumns"
                    :active-columns="knowledgeDocumentsPaginated.activeColumns"
                    :page-info="knowledgeDocumentsPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    template(v-slot:type="{ value }")
                        el-tag(size="mini" type="info") {{ $t('type.' + value) }}
                    template(v-slot:source="{ value }")
                        el-tag(
                            size="mini"
                            :type="sourceTagType(value)"
                        ) {{ $t('source.' + value) }}
                    template(v-slot:body="{ value }")
                        span {{ truncate(value, 100) }}
                    template(v-slot:last_synced_at="{ value }")
                        span {{ formatTimestamp(value) }}
                    template(v-slot:created_at="{ value }")
                        span {{ formatTimestamp(value) }}
                    template(v-slot:updated_at="{ value }")
                        span {{ formatTimestamp(value) }}
                    template(v-slot:actions="{ item }")
                        el-tooltip(v-if="item.source === 'manual'" :content="$t('Delete')" placement="top")
                            el-button(
                                plain
                                circle
                                size="mini"
                                type="danger"
                                icon="el-icon-fa-trash-alt fas"
                                @click.stop.prevent="openDeleteDialog(item)"
                            )
                    template(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .left
                                el-button(
                                    type="primary"
                                    size="mini"
                                    icon="el-icon-fa-plus fas"
                                    @click="openEntryDrawer()"
                                ) {{ $t('New Entry') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisibility"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}

        el-dialog(
            :title="$t('Delete Entry')"
            :visible.sync="deleteDialog.visibility"
            :close-on-click-modal="false"
            top="10vh"
            width="460px"
        )
            .body(v-loading="deleteDialog.loading")
                p {{ $t('Do you really want to delete the entry?') }}
            template(slot="footer")
                .modal-footer
                    .left
                        el-checkbox(v-model="deleteDialog.remember") {{ $t("Don't keep asking") }}
                    .right
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            :disabled="deleteDialog.loading"
                            @click="closeDeleteDialog"
                        ) {{ $t('Cancel') }}
                        el-button(
                            size="mini"
                            type="danger"
                            icon="el-icon-fa-trash-alt fas"
                            :disabled="deleteDialog.loading"
                            @click="() => deleteEntry(deleteDialog.entry)"
                        ) {{ $t('Delete') }}

        el-drawer.full-height-drawer(
            :title="drawerTitle"
            :visible.sync="entryDrawerVisible"
            :append-to-body="true"
            size="500px"
        )
            .drawer-body.no-tabs
                .block-content
                    template(v-if="isReadOnly")
                        .entry-read-only-field
                            .entry-read-only-label {{ $t('Title') }}
                            .entry-read-only-value {{ entryForm.title }}
                        .entry-read-only-field
                            .entry-read-only-label {{ $t('Type') }}
                            .entry-read-only-value
                                el-tag(size="mini" type="info") {{ $t('type.' + editingDoc.type) }}
                        template(v-if="documentMetadata.length")
                            .entry-read-only-field(v-for="meta in documentMetadata" :key="meta.key")
                                .entry-read-only-label {{ $t('meta.' + meta.key) }}
                                .entry-read-only-value.meta-value {{ meta.value }}
                        .entry-read-only-field(v-if="entryForm.body")
                            .entry-read-only-label {{ $t('Content') }}
                            .entry-read-only-value.entry-read-only-body {{ entryForm.body }}
                        .entry-read-only-field(v-else)
                            .entry-read-only-value.cms-note {{ $t('This document is indexed from CMS content and cannot be edited here.') }}
                    el-form(v-else label-position="top" :model="entryForm" :rules="entryRules" ref="entryForm" @submit.native.prevent="saveEntry")
                        el-form-item(:label="$t('Title')" prop="title")
                            el-input(v-model="entryForm.title" :maxlength="255" show-word-limit)
                        el-form-item(:label="$t('Content')" prop="body")
                            el-input(
                                v-model="entryForm.body"
                                type="textarea"
                                :autosize="{ minRows: 4 }"
                                :maxlength="5000"
                                show-word-limit
                            )
            .drawer-footer
                el-button(
                    round
                    size="mini"
                    icon="el-icon-fa-times fas"
                    @click="entryDrawerVisible = false"
                ) {{ isReadOnly ? $t('Close') : $t('Cancel') }}
                el-button(
                    v-if="!isReadOnly"
                    round
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="entrySaving"
                    @click="saveEntry"
                ) {{ editingDoc ? $t('Update') : $t('Create') }}
</template>

<i18n>
{
    "en": {
        "type.page": "Page",
        "type.product": "Product",
        "type.event": "Event",
        "type.business_card": "Business Card",
        "type.public_address": "Address",
        "type.manual": "Manual",
        "type.external": "External",
        "source.cms": "CMS Content",
        "source.manual": "Manual",
        "source.external": "External",
        "meta.url": "URL",
        "meta.brand": "Brand",
        "meta.condition": "Condition",
        "meta.available_for_groups": "Available for"
    },
    "de": {
        "No knowledge source configured for this CMS.": "Keine Wissensquelle für dieses CMS konfiguriert.",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "New Entry": "Neuer Eintrag",
        "Title": "Titel",
        "Type": "Typ",
        "Content": "Inhalt",
        "Edit Entry": "Eintrag bearbeiten",
        "View Document": "Dokument anzeigen",
        "Create": "Erstellen",
        "Update": "Aktualisieren",
        "Cancel": "Abbrechen",
        "Close": "Schließen",
        "Delete": "Löschen",
        "Delete Entry": "Eintrag löschen",
        "Do you really want to delete the entry?": "Möchten Sie den Eintrag wirklich löschen?",
        "This document is indexed from CMS content and cannot be edited here.": "Dieses Dokument wurde aus den CMS-Inhalten indexiert und kann hier nicht bearbeitet werden.",
        "type.page": "Seite",
        "type.product": "Produkt",
        "type.event": "Veranstaltung",
        "type.business_card": "Visitenkarte",
        "type.public_address": "Adresse",
        "type.manual": "Manuell",
        "type.external": "Extern",
        "source.cms": "CMS-Inhalt",
        "source.manual": "Manuell",
        "source.external": "Extern",
        "Indexed metadata": "Indexierte Metadaten",
        "meta.url": "URL",
        "meta.brand": "Marke",
        "meta.condition": "Zustand",
        "meta.available_for_groups": "Verfügbar für",
        "yyyy-MM-dd": "dd.MM.yyyy",
        "Entry deleted successfully": "Eintrag erfolgreich gelöscht",
        "Entry could not be deleted": "Eintrag konnte nicht gelöscht werden",
        "Entry saved successfully": "Eintrag erfolgreich gespeichert",
        "Entry could not be saved": "Eintrag konnte nicht gespeichert werden",
        "Knowledge documents could not be fetched.": "Wissensdokumente konnten nicht abgerufen werden.",
        "An error occurred": "Ein Fehler ist aufgetreten",
        "Form is not valid!": "Formular ist nicht gültig!",
        "Correct your input and submit again.": "Korrigieren Sie Ihre Eingabe und senden Sie erneut."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';
import { timestamp2date } from '@/filters';

import pinia from '@/pinia';
import { useSetupStore, useSettingsStore, useKnowledgeDocumentStore, useKnowledgeEntryStore } from '@/stores';

import PaginatedItems from '@/components/PaginatedItems';
import Error from '@/components/Error';
import AiToolsTabs from '@/components/AiToolsTabs';

export default {
    name: 'KnowledgeBase',

    components: {
        PaginatedItems,
        Error,
        AiToolsTabs
    },

    data() {
        return {
            error: false,
            settingsLoading: true,
            disable: {
                showFilters: false
            },
            entryForm: { title: '', body: '' },
            editingDoc: null,
            entrySaving: false,
            entryDrawerVisible: false,
            deleteDialog: {
                visibility: false,
                loading: false,
                remember: false,
                entry: null
            }
        };
    },

    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.knowledgeDocumentsPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        filters() {
            return _.cloneDeep(this.knowledgeDocumentsPaginated.filters);
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        settingsStore() {
            return useSettingsStore();
        },
        setupStore() {
            return useSetupStore();
        },
        documentStore() {
            return useKnowledgeDocumentStore();
        },
        entryStore() {
            return useKnowledgeEntryStore();
        },
        loading() {
            return this.documentStore.loading;
        },
        knowledgeDocumentsPaginated() {
            return this.documentStore.knowledgeDocumentsPaginated;
        },
        knowledgeSource() {
            return _.get(this.settingsStore.form, 'knowledgeSource') || null;
        },
        knowledgeSourceId() {
            return _.get(this.knowledgeSource, 'id') || null;
        },
        isReadOnly() {
            return !!(this.editingDoc && this.editingDoc.source !== 'manual');
        },
        documentMetadata() {
            return _.get(this.editingDoc, 'metadata') || [];
        },
        drawerTitle() {
            if (this.isReadOnly) return this.$t('View Document');
            return this.editingDoc ? this.$t('Edit Entry') : this.$t('New Entry');
        },
        sort() {
            const variables = this.knowledgeDocumentsPaginated.variables;
            const defaultSort = this.knowledgeDocumentsPaginated.defaultSort || { field: 'title', direction: 'asc' };
            const order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            const direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        },
        entryRules() {
            return {
                title: 'required',
                body: 'required'
            };
        }
    },

    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) this.loadSettingsIfNeeded();
        },
        knowledgeSourceId: {
            immediate: true,
            handler(id) {
                this.documentStore.setKnowledgeSourceId(id);
                this.entryStore.setKnowledgeSourceId(id);
                if (id) this.refetch();
            }
        }
    },

    methods: {
        loadSettingsIfNeeded() {
            const cmsId = _.get(this.activeCms, 'id');
            if (!cmsId) {
                this.settingsLoading = false;
                return;
            }

            const formId = _.get(this.settingsStore, 'form.id');
            if (formId && String(formId) === String(cmsId)) {
                this.settingsLoading = false;
                return;
            }

            this.settingsLoading = true;
            this.settingsStore.fetchCms(cmsId)
                .finally(() => { this.settingsLoading = false; });
        },
        sourceTagType(source) {
            if (source === 'manual') return 'success';
            if (source === 'external') return 'warning';
            return 'info';
        },
        truncate(text, length) {
            if (!text) return '';
            return text.length > length ? text.substring(0, length) + '...' : text;
        },
        formatTimestamp(timestamp) {
            if (!timestamp) return '-';
            return timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm');
        },
        handleClick(item) {
            this.openEntryDrawer(item);
        },
        handleSizeChange(itemsPerPage) {
            this.documentStore.updateLoading(true);
            this.documentStore.defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.documentStore.updateLoading(true);
            this.documentStore.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.documentStore.defineActiveColumns(visibleColumns);
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.documentStore.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.documentStore.defineSorting({ key, value });
            this.refetch();
        },
        toggleFilterVisibility() {
            const visible = this.showFilters;
            const value = !visible;

            this.disable.showFilters = true;
            this.documentStore.defineFilterVisibility(value);
            this.saveFilterVisibility({ scope: 'ex', key: 'chatbot_knowledge_document', value })
                .finally(() => { this.disable.showFilters = false; });
        },
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
        refetch() {
            if (!this.knowledgeSourceId) return;

            this.documentStore.updateLoading(true);
            this.documentStore.fetchKnowledgeDocumentsPaginated()
                .then(() => this.documentStore.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err;

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Knowledge documents could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.documentStore.updateLoading(false);
                    console.error(err);
                });
        },
        debouncedRefetch: _.debounce(function() {
            this.documentStore.updateLoading(true);
            this.refetch();
        }, useSetupStore(pinia).debounce || 300),
        openEntryDrawer(doc = null) {
            this.editingDoc = doc;
            this.entryForm = doc
                ? { title: doc.title, body: doc.body }
                : { title: '', body: '' };
            this.entryDrawerVisible = true;
            this.$nextTick(() => {
                if (this.$refs.entryForm) {
                    this.$refs.entryForm.clearValidate();
                }
            });
        },
        saveEntry() {
            if (this.isReadOnly) return;
            this.$refs.entryForm.validate(valid => {
                if (!valid) {
                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                    return;
                }

                this.entrySaving = true;

                const promise = (this.editingDoc && this.editingDoc.knowledge_entry_id)
                    ? this.entryStore.updateKnowledgeEntry({ id: this.editingDoc.knowledge_entry_id, title: this.entryForm.title, body: this.entryForm.body })
                    : this.entryStore.createKnowledgeEntry({ title: this.entryForm.title, body: this.entryForm.body });

                promise
                    .then(() => {
                        this.$notify.success({
                            title: this.$t('Entry saved successfully'),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 4000
                        });
                        this.entryForm = { title: '', body: '' };
                        this.editingDoc = null;
                        this.entryDrawerVisible = false;
                        this.refetch();
                    })
                    .catch(() => {
                        this.$notify.error({
                            title: this.$t('Entry could not be saved'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    })
                    .finally(() => {
                        this.entrySaving = false;
                    });
            });
        },
        openDeleteDialog(doc) {
            if (this.deleteDialog.remember) {
                this.deleteEntry(doc);
            } else {
                this.deleteDialog.entry = doc;
                this.deleteDialog.visibility = true;
            }
        },
        closeDeleteDialog() {
            this.deleteDialog.visibility = false;
            this.deleteDialog.loading = false;
            this.deleteDialog.entry = null;

            setTimeout(() => {
                this.deleteDialog.remember = false;
            }, 300);
        },
        deleteEntry(doc) {
            if (!doc || !doc.knowledge_entry_id) return;

            this.deleteDialog.loading = true;
            this.entryStore.deleteKnowledgeEntry(doc.knowledge_entry_id)
                .then(() => {
                    this.$notify.success({
                        title: this.$t('Entry deleted successfully'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 4000
                    });
                    this.closeDeleteDialog();
                    this.refetch();
                })
                .catch(() => {
                    this.$notify.error({
                        title: this.$t('Entry could not be deleted'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.deleteDialog.loading = false;
                });
        }
    },

    mounted() {
        this.documentStore.updateLoading(true);
        this.loadSettingsIfNeeded();
    }
};
</script>

<style lang="sass" scoped>
.error
    padding: 20px

.not-configured
    padding: 40px
    text-align: center
    color: #909399

.drawer-footer
    padding: 12px 20px
    border-top: 1px solid #e4e7ed
    display: flex
    justify-content: flex-end
    gap: 8px

.entry-read-only-field
    margin-bottom: 18px

.entry-read-only-label
    font-size: 13px
    color: #606266
    margin-bottom: 6px

.entry-read-only-value
    font-size: 14px
    color: #303133
    line-height: 1.6

.entry-read-only-body
    white-space: pre-wrap
    word-break: break-word

.cms-note
    color: #909399
    font-style: italic

.meta-value
    word-break: break-word
</style>
